<template>
  <div>
    <div class="mb-1">
      <h3 class="title is-5 mb-2">Carga de Datos desde Excel</h3>
      <p class="has-text-grey">
        Sube un archivo Excel con los datos de los colaboradores.
      </p>
    </div>

    <!-- Download Template -->
    <div class="template-box mb-2">
      <div class="template-content">
        <div>
          <h4 class="has-text-weight-semibold">Plantilla de Excel</h4>
          <p class="is-size-7 has-text-grey">
            Descarga la plantilla con el formato correcto para cargar los datos
          </p>
        </div>
        <b-button icon-left="download" @click="askAndDownloadTemplate">
          Descargar Plantilla
        </b-button>
      </div>
    </div>

    <!-- Drag & Drop Area -->
    <div
      :class="['upload-area', { 'is-dragging': dragActive }]"
      @dragenter.prevent="handleDrag"
      @dragleave.prevent="handleDrag"
      @dragover.prevent="handleDrag"
      @drop.prevent="handleDrop"
    >
      <b-icon
        icon="file-excel"
        size="is-large"
        custom-class="has-text-grey mb-4"
      />
      <h4 class="title is-5 mb-2">Arrastra tu archivo Excel aquí</h4>
      <p class="has-text-grey mb-4 is-size-7">
        O haz clic para seleccionar un archivo (.xlsx, .xls)
      </p>
      <b-upload
        v-model="uploadFile"
        accept=".xlsx,.xls"
        @input="handleFileUpload"
      >
        <b-button type="is-primary" icon-left="upload" tag="a">
          Seleccionar Archivo
        </b-button>
      </b-upload>
    </div>

    <!-- Instructions -->
    <b-message
      v-if="!response.errors && !response.data"
      type="is-info"
      class="mt-2"
    >
      <h4 class="has-text-weight-semibold mb-2">
        Instrucciones de Carga
      </h4>
      <ul class="instruction-list">
        <li>• El archivo debe estar en formato Excel (.xlsx o .xls)</li>
        <li>
          • Usa la plantilla proporcionada para asegurar el formato correcto
        </li>
        <li>
          • Los datos existentes serán reemplazados por los nuevos
        </li>
        <li>• Revisa los datos antes de confirmar la carga</li>
      </ul>
    </b-message>

    <!-- Success upload -->
    <b-message
      v-if="response.data && response.data.length"
      type="is-success"
      class="mt-2"
    >
      <h4 class="has-text-weight-semibold mb-2">
        Archivo cargado correctamente
      </h4>
      <ul class="instruction-list">
        <li>
          • Se cargo correctamente
          {{ response.data.filter((d) => !d.ifUpdated).length }} Colaborador{{
            response.data.filter((d) => !d.ifUpdated).length > 1 ? "es" : ""
          }}
        </li>
        <li>
          • Se actualizo correctamente
          {{ response.data.filter((d) => d.ifUpdated).length }} Colaborador{{
            response.data.filter((d) => d.ifUpdated).length > 1 ? "es" : ""
          }}
        </li>
      </ul>
    </b-message>

    <!-- Erros in File -->
    <b-message
      v-if="response.errors && response.errors.length"
      type="is-warning"
      class="mt-2"
    >
      <h4 class="has-text-weight-semibold mb-2">
        Errores en la carga
      </h4>
      <ul class="instruction-list">
        <li v-for="(item, index) in response.errors" :key="index">
          <div v-if="item.type === 'columns_missing'">
            {{ item.error }} -> {{ JSON.stringify(item.columns) }}
          </div>
          <div v-else-if="item.type === 'column_type'">
            • {{ item.column }} (Fila -> {{ item.row }}) - {{ item.error }}
          </div>
          <div v-else>• {{ item.error }}</div>
        </li>
      </ul>
    </b-message>
  </div>
</template>
<script>
import { fetchDependencies, uploadCollaborators, createDependences } from "../../api";
import store from "../../store";

const XLSX = require("xlsx");
const schemaQuestions = [
  'Cl_vl','Ad_vl','Je_vl','Me_vl',
  'Me_cd','Je_cd','Cl_cd','Ad_cd',
  'Ad_lo','Me_lo','Cl_lo','Je_lo',
  'Ad_ee','Je_ee','Cl_ee','Me_ee',
  'Cl_ce','Me_ce','Je_ce','Ad_ce',
  'Ad_sp','Je_sp','Me_sp','Cl_sp',
  'Cl_vl2','Ad_vl2','Je_vl2','Me_vl2',
  'Me_cd2','Je_cd2','Cl_cd2','Ad_cd2',
  'Ad_lo2','Me_lo2','Cl_lo2','Je_lo2',
  'Ad_ee2','Je_ee2','Cl_ee2','Me_ee2',
  'Cl_ce2','Me_ce2','Je_ce2','Ad_ce2',
  'Ad_sp2','Je_sp2','Me_sp2','Cl_sp2'
]

const schemaWords = [
  'frase1es',
  'frase2es',
  'frase3es',
  'frase4es',
  'frase5es',
  'frase6deberia',
  'frase7deberia',
  'frase8deberia',
  'frase9deberia',
  'frase10deberia'
]

export default {
  name: "UploadCollaborators",
  data() {
    return {
      response: {
        data: null,
        errors: null,
      },
      dragActive: false,
      uploadFile: null,
    };
  },
  computed: {
    currentProcess() {
      return store.state.selectedProcess;
    },
  },
  methods: {
    async askAndDownloadTemplate() {
      const result = await this.$swal({
        title: "Descargar plantilla",
        html: `
          <p>¿Deseas incluir las columnas de <strong>preguntas</strong> y <strong>palabras</strong> en la plantilla?</p>
          <p style="font-size:0.85rem;color:#888;margin-top:0.5rem">Esto es útil si deseas cargar respuestas de encuesta junto con los colaboradores.</p>
        `,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Sí, incluir",
        cancelButtonText: "No, solo datos básicos",
        cancelButtonColor: "#3085d6",
      });
      this.downloadTemplate(result.isConfirmed);
    },
    async downloadTemplate(includeSchema = false) {
      const dependencies = await fetchDependencies(this.currentProcess._id);

      this.$buefy.toast.open({
        message: "Descargando plantilla...",
        type: "is-info",
      });

      const headers = [
        [
          "Nombre",
          "Correo",
          "Cedula",
          "Tipo",
          ...dependencies.map((d) => d.name),
          ...(includeSchema ? [...schemaQuestions, ...schemaWords] : []),
        ],
      ];

      const ws = XLSX.utils.aoa_to_sheet(headers);
      const wb = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(wb, ws, "Colaboradores");
      XLSX.writeFile(wb, "Plantilla_Colaboradores.xlsx");
    },
    handleDrag(e) {
      if (e.type === "dragenter" || e.type === "dragover") {
        this.dragActive = true;
      } else if (e.type === "dragleave") {
        this.dragActive = false;
      }
    },
    handleDrop(e) {
      this.dragActive = false;
      const files = e.dataTransfer.files;

      if (files && files[0]) {
        const file = files[0];
        if (file.type.includes("sheet") || file.type.includes("excel")) {
          this.saveFile(file);
        } else {
          this.$buefy.dialog.alert({
            title: "Error",
            message: "Por favor, sube solo archivos Excel (.xlsx o .xls)",
            type: "is-danger",
          });
        }
      }
    },
    handleFileUpload(file) {
      if (file) {
        this.saveFile(file);
      }
    },
    getFileHeaders(file) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const wb = XLSX.read(e.target.result, { type: "array" });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
          resolve(rows[0] || []);
        };
        reader.readAsArrayBuffer(file);
      });
    },
    saveFile(file) {
      const config = {
        title: "¿Continuar con la carga?",
        html: `
        <p>Estás subiendo el archivo:</p>
        <strong>${file.name}</strong>
        `,
        icon: "info",
        showCancelButton: true,
        confirmButtonText: "Sí, continuar",
        cancelButtonText: "Cancelar",
        cancelButtonColor: "#de5b5b",
      };
      this.$swal(config).then(async (result) => {
        if (result.isConfirmed) {
          const headers = await this.getFileHeaders(file);
          const existingDeps = await fetchDependencies(this.currentProcess._id);
          const knownColumns = [
            "Nombre", "Correo", "Cedula", "Tipo",
            ...schemaQuestions,
            ...schemaWords,
            ...existingDeps.map((d) => d.name),
          ];
          const unknownColumns = headers.filter((h) => !knownColumns.includes(h));

          if (unknownColumns.length) {
            const depConfirm = await this.$swal({
              title: "Nuevas dependencias detectadas",
              html: `
                <p>Las siguientes columnas no están registradas como dependencias:</p>
                <strong>${unknownColumns.join(", ")}</strong>
                <p style="margin-top:0.75rem;font-size:0.85rem;color:#888">¿Deseas crearlas automáticamente como dependencias de tipo texto?</p>
              `,
              icon: "question",
              showCancelButton: true,
              confirmButtonText: "Sí, crear y continuar",
              cancelButtonText: "Cancelar carga",
              cancelButtonColor: "#de5b5b",
            });

            if (!depConfirm.isConfirmed) {
              this.uploadFile = null;
              return;
            }

            await Promise.all(
              unknownColumns.map((name) =>
                createDependences({
                  idProcess: this.currentProcess._id,
                  name,
                  type: "text",
                  options: null,
                })
              )
            );
          }

          await this.$fire
            .tempFiles(file)
            .then((snapshot) => {
              snapshot.ref
                .getDownloadURL()
                .then((url) => {
                  uploadCollaborators(url, this.currentProcess._id).then(
                    (res) => {
                      const hasErrors = !!(res.errors && res.errors.length);
                      const hasData = !!(res.data && res.data.length);
                      if (hasErrors && !hasData) {
                        this.uploadFile = null;
                        this.$buefy.toast.open({
                          message: `Se encontraron errores en el archivo`,
                          type: "is-warning",
                        });
                        this.response = { ...res };
                        return;
                      }

                      this.uploadFile = null;
                      this.$buefy.toast.open({
                        message: `Archivo cargado correctamente`,
                        type: "is-success",
                      });

                      this.response = { ...res };
                    }
                  );
                })
                .catch((err) => {
                  this.uploadFile = null;
                  console.log(err);
                });
            })
            .catch((err) => console.log(err));
        } else {
          this.uploadFile = null;
        }
      });
    },
  },
};
</script>

<style scoped>
/* Upload Area */
.upload-area {
  border: 2px dashed #dbdbdb;
  border-radius: 6px;
  padding: 2rem;
  text-align: center;
  transition: all 0.2s;
}

.upload-area:hover {
  border-color: #b5b5b5;
}

.upload-area.is-dragging {
  border-color: #00d1b2;
  background-color: rgba(0, 209, 178, 0.05);
}

/* Template Box */
.template-box {
  background-color: rgba(245, 245, 245, 0.8);
  border-radius: 6px;
  padding: 1rem;
}

.template-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.instruction-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.instruction-list li {
  font-size: 0.875rem;
  line-height: 1.6;
}
</style>



// WEBPACK FOOTER //
// src/components/cultura/sections/configuration/UploadCollaborators.vue